import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhanceTableHeaderTypes,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import Loader from 'src/components/Loader';
import { DataService } from 'src/services/data-service';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { useAppDispatch } from 'src/redux/hooks';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { Button, Typography } from '@mui/material';

interface IPageListing {
  id: string;
  code: string;
  title: string;
  route: string;
  active: string;
}

interface IPageListingResponse {
  id: string;
  code: string;
  title: string;
  route: string;
  active: string;
}

const PageListing: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<Record<string, IPageListing>>();

  const headers: EnhanceTableHeaderTypes[] = [
    { 
      id: 'code', 
      label: 'Route', 
      type: EnhancedTableColumnType.Clickable, 
      onClick: (_: string, row: Record<string, any>) => { 
        handleEdit(row.id);
      } 
    },
    { id: 'title', label: 'Title', type: EnhancedTableColumnType.TEXT },
    // { id: 'route', label: 'Route', type: EnhancedTableColumnType.TEXT },
    {
      id: 'active',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    }
  ];

  const initialize = async () => {
    setLoading(true);
    const response = await DataService.get('api/page/all');
    if (response.ok) {
      const list: IPageListingResponse[] = await response.json();
      const result: Record<string, IPageListing> = {};
      list.forEach((c) => {
        result[c.id] = {
          id: c.id,
          code: c.code,
          title: c.title,
          route: c.route,
          active: c.active ? 'Active' : 'Inactive'
        };
      });
      setData(result);
    } else {
      toast.error('Failed to fetch pages.');
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  if (loading) {
    return <Loader size={75} />;
  }

  // Navigate to Edit Page
  const handleEdit = (id: string) => {
    navigate(`/web-builder/pages/edit/${id}`);
  };

  // Navigate to Add Page
  const handleAdd = () => {
    navigate('/web-builder/pages/add');
  };

  const actions: ITableMenuAction[] = [
    {
      key: 'addpage',
      title: 'Add Page',
      disabled: (selected: string[]) => selected.length > 0,
      onClick: handleAdd
    },
    {
      key: 'editpage',
      title: 'Edit Page',
      disabled: (selected: string[]) => selected.length === 0,
      onClick: (selected) => {
        if (selected.length > 0) {
          handleEdit(selected[0]);
        }
      }
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Page Listing</title>
      </Helmet>
      <MainPageTitle
        title="Page List"
        action={{
          title: 'Add Page',
          onClick: handleAdd
        }}
      />

      <EnhancedTable title="Pages" header={headers} data={data} actions={actions} />
  </div>
  );
};

export default PageListing;
